import { forwardRef, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import showPasswordIcon from "../../assets/ShowPasswordIcon.svg";
import hidePasswordIcon from "../../assets/hidePassword.svg";
import "./style.css";
import PI, { PhoneInputProps } from "react-phone-input-2";
import { GetImages } from "../../utils/GetImages";
import { useError } from "../../utils/context/ErrorContext";
import { validatePhone, validateEmail } from "../../utils/validateHelper";
import { formatNumberForInput, formatWithCommas } from "../../utils/helper";
import { useOnboardingContext } from "../../utils/context/OnboardingContext";
import { TextCss } from "../GlobalCss/TextCss";
import { useCurrencySymbol } from "../../hooks/useCurrencySymbol";
const ReactPhoneInput: React.FC<PhoneInputProps> = (PI as any).default || PI;

const CustomInputs = forwardRef<HTMLInputElement, any>(
  (
    {
      labelText = "",
      label_img = undefined,
      value = undefined,
      key = undefined,
      onChange,
      onBlur = (value: any): any => {},
      onFocus = () => {},
      errorMessages = "",
      id = undefined,
      name = undefined,
      type = "text",
      placeholder = "",
      isPassword = false,
      focus = false,
      disabled = false,
      required = false,
      maxLength = undefined,
      myClassName = "",
      isFormControl = false,
      isError = false,
      min = undefined,
      max = undefined,
      onImageUpload = undefined,
      currencySymbol = undefined,
      checked = false,
      height = undefined,
      multiple = false,
      rows = undefined,
      width = undefined,
      currencyHeight = undefined,
      InputsWrapperClass = undefined,
      containerClassName = "singleInputsWrapper",
      currencySymbolDirection = "left",
      maxWidth = undefined,
      linkWebsite = false,
      title = undefined,
      sectionName = "",
      inputIndex = undefined,
      onKeyDown = undefined,
      mainContainerClass = "",
      isCurrency = false,
    },
    ref,
  ) => {
    currencySymbol = useCurrencySymbol();
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const inputRef = useRef(null);
    const validationTimer = useRef<any>(null);
    const { errors, setErrors, setErrorMessages } = useError();
    const { setCountryCode } = useOnboardingContext();

    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    const handleChange = (event) => {
      if (type === "number") {
        let value = event.target.value;
        if (value < 0) {
          value = 0; // Reset negative values to zero
        }
        onChange({ target: { name: event.target.name, value } });
      } else if (type === "tel") {
        const numericValue = event.target.value.replace(/[^0-9]/g, "");
        onChange({ target: { name: event.target.name, value: numericValue } });
      } else if (type === "file" && onImageUpload) {
        onImageUpload(event);
      } else if (type === "date") {
        onChange(event);
        // isSafari && event.target.blur(); // This will close the date picker in Safari
      } else {
        onChange(event);
      }
    };

    const handlePhoneChange = (event, phone, country) => {
      onChange({
        target: {
          name: name || "phoneNumber",
          value: event,
          phone: phone,
          country: country,
        },
      });
    };

    const handleBlur = (event) => {
      onBlur(event);
      if (type == "email") {
        const key = sectionName ? `${sectionName}.${name}` : name;
        validateEmail(value, setErrors, setErrorMessages, key);
      }
    };

    const handlePhoneKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== "Backspace" && event.key !== "Delete") return;
      const input = event.target as HTMLInputElement;
      const fullySelected =
        input.selectionStart === 0 &&
        input.selectionEnd === input.value.length &&
        input.value.length > 0;
      if (!fullySelected) return;
      event.preventDefault();
      handlePhoneChange("", "", { countryCode: "us" });
      const errorKey =
        inputIndex !== undefined ? `phoneNumber[${inputIndex}]` : name || "phoneNumber";
      setErrors((prev: any) => ({ ...prev, [errorKey]: false }));
      setErrorMessages((prev: any) => ({ ...prev, [errorKey]: "" }));
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (type === "number" && ["+", "-", ".", "e"].includes(event.key)) {
        event.preventDefault();
      }
    };

    const handleCurrencyKeyDown = (
      e: React.KeyboardEvent<HTMLInputElement>,
    ) => {
      const key = e.key;

      const allowedKeys = [
        "Backspace",
        "Tab",
        "Delete",
        "ArrowLeft",
        "ArrowRight",
        "Home",
        "End",
      ];

      if (allowedKeys.includes(key)) return;
      if (e.ctrlKey || e.metaKey) return;

      const isNumber = /^[0-9]$/.test(key);
      const isDot = key === ".";

      if (isDot) {
        if ((e.target as HTMLInputElement).value.includes(".")) {
          e.preventDefault();
        }
        return;
      }

      if (!isNumber && !isDot) e.preventDefault();
    };

    const handleCurrencyChange = (e) => {
      const rawVal = e.target.value.replace(/,/g, "");
      const MAX_LIMIT = 99999999.99;

      // 1. Decimal restriction (max 2)
      if (rawVal.includes(".") && rawVal.split(".")[1].length > 2) {
        return;
      }

      // 2. Max Value hard-block
      const numericVal = parseFloat(rawVal);
      if (!isNaN(numericVal) && numericVal > MAX_LIMIT) {
        const errorKey = name;
        setErrors((prev: any) => ({ ...prev, [errorKey]: true }));
        setErrorMessages((prev: any) => ({
          ...prev,
          [errorKey]: `Amount cannot exceed $${formatWithCommas(MAX_LIMIT)}`,
        }));

        // Auto-clear error after 1 second of inactivity
        if (validationTimer.current) clearTimeout(validationTimer.current);
        validationTimer.current = setTimeout(() => {
          setErrors((prev: any) => ({ ...prev, [errorKey]: undefined }));
          setErrorMessages((prev: any) => ({ ...prev, [errorKey]: undefined }));
        }, 1000);

        return;
      }

      // Call parent onChange
      onChange({ target: { name, value: rawVal } });
    };

    const toggleShowPassword = (): void => {
      setShowPassword(!showPassword);
    };

    let injectErrorClasses = "";
    if (typeof isError === "boolean") {
      injectErrorClasses = isError
        ? "errorBorder errorOutline"
        : "normalBorder normalOutline";
    }

    return (
      <div className={`${mainContainerClass} custom_input`}>
        {labelText && (
          <div className="lablelClass">
            <p className={TextCss.black.sub_heading_600_14}>
              {labelText}
              {required && <span className="red">*</span>}
            </p>

            {label_img && <img src={label_img} height={16} />}
          </div>
        )}

        {isCurrency ? (
          <div
            className="currency-input-wrapper"
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              marginBottom: isError ? "0px" : "8px",
            }}
          >
            <span
              style={{
                position: "absolute",
                left: "10px",
                color: "var(--grey-subtext1)",
                fontWeight: 500,
                pointerEvents: "none",
              }}
            >
              {currencySymbol || "$"}
            </span>
            <input
              type="text"
              disabled={disabled}
              placeholder={placeholder}
            className={`${injectErrorClasses} ${myClassName} ${TextCss.sub_heading_400_14} inputClass form-control`}
              style={{
                paddingLeft: "35px",
                height: height,
                border: isError
                  ? "2px solid #F05252"
                  : "1px solid var(--Grey-Divider)",
                marginBottom: "0px",
                outlineColor: isError ? "#E02424" : "var(--Primary-Regular)",
                backgroundColor: "var(--bg-secondary)",
                color: "var(--Grey-Heading)",
              }}
              onKeyDown={handleCurrencyKeyDown}
              value={formatNumberForInput(value?.toString() || "")}
              onChange={handleCurrencyChange}
              ref={ref}
              onMouseLeave={() => {
                const errorKey = name;
                setErrors((prev: any) => ({ ...prev, [errorKey]: undefined }));
                setErrorMessages((prev: any) => ({
                  ...prev,
                  [errorKey]: undefined,
                }));
              }}
            />
          </div>
        ) : type == "mobile" ? (
          <ReactPhoneInput
            country="us"
            onlyCountries={["us"]}
            disableDropdown={true}
            countryCodeEditable={false}
            value={value ?? ""}
            onChange={(phone, country, e, formattedValue) => {
              handlePhoneChange(formattedValue, phone, country);
              setCountryCode(country);
              validatePhone(
                phone,
                country,
                setErrors,
                setErrorMessages,
                sectionName,
                inputIndex,
              );
            }}
            onBlur={handleBlur}
            onKeyDown={handlePhoneKeyDown}
            copyNumbersOnly={true}
            containerClass="phoneInputContainer normal_text"
            inputClass="phoneInputClass"
            buttonClass="phoneInputButton"
            containerStyle={{
              border: isError
                ? "1px solid #F05252"
                : "1px solid var(--Grey-Divider)",
              marginBottom: isError ? "0px" : "8px",
              outlineColor: isError ? "#E02424" : "var(--Primary-Regular)",
              maxWidth: maxWidth,
              width: width,
              backgroundColor: "var(--bg-secondary)",
            }}
            buttonStyle={{
              backgroundColor: "var(--bg-secondary)",
              borderColor: "var(--Grey-Divider)",
            }}
          />
        ) : type === "date" ? (
          <input
            type="date"
            id={id}
            name={name}
            className={`${injectErrorClasses} ${myClassName} ${TextCss.sub_heading_400_14} ${type == "checkbox" ? "checkboxclass" : "inputClass"} form-control `}
            defaultValue={value ?? ""}
            placeholder={placeholder}
            onBlur={handleBlur}
            onChange={onChange}
            disabled={disabled}
            autoFocus={focus}
            min={min}
            onFocus={onFocus}
            title={title}
            ref={inputRef}
            style={{
              border: isError
                ? "2px solid #F05252"
                : "1px solid var(--Grey-Divider)",
              marginBottom: isError ? "0px" : "8px",
              outlineColor: isError ? "#E02424" : "var(--Primary-Regular)",
              height: height,
              backgroundColor: "var(--bg-secondary)",
              color: "var(--Grey-Heading)",
            }}
            onKeyDown={onKeyDown ? onKeyDown : handleKeyDown}
          />
        ) : (
          <div
            className="passwordInputBox"
            // style={{ width: width, maxWidth: maxWidth }}
          >
            <input
              id={id}
              className={`${injectErrorClasses} ${myClassName} ${TextCss.sub_heading_400_14} ${type == "checkbox" ? "checkboxclass" : "inputClass"} form-control `}
              type={isPassword ? (showPassword ? "text" : "password") : type}
              autoComplete={isPassword ? "current-password" : "off"}
              name={name}
              value={type === "file" ? undefined : (value ?? "")}
              placeholder={placeholder}
              onBlur={handleBlur}
              onChange={handleChange}
              disabled={disabled}
              autoFocus={focus}
              min={min}
              maxLength={
                type == "email"
                  ? 50
                  : type == "textarea"
                    ? 200
                    : maxLength
                      ? maxLength
                      : 30
              }
              onFocus={onFocus}
              title={title}
              ref={type === "date" ? inputRef : ref}
              style={{
                border: isError
                  ? "2px solid #F05252"
                  : "1px solid var(--Grey-Divider)",
                marginBottom: isError ? "0px" : "8px",
                outlineColor: isError ? "#E02424" : "var(--Primary-Regular)",
                height: height,
                backgroundColor: "var(--bg-secondary)",
                color: "var(--Grey-Heading)",
              }}
              onKeyDown={onKeyDown ? onKeyDown : handleKeyDown}
            />
            {(isPassword || linkWebsite) && (
              <span
                onClick={
                  linkWebsite
                    ? () => window.open(value, "_blank")
                    : toggleShowPassword
                }
                className="showPasswordIcon"
                style={{
                  backgroundImage: `url(${linkWebsite ? GetImages.goToWebsiteIcon : showPassword ? hidePasswordIcon : showPasswordIcon})`,
                  transform: isError ? "translateY(-40%)" : "translateY(-60%)",
                  top: "45%",
                }}
              />
            )}
          </div>
        )}

        {isError && (
          <div
          // style={{ marginTop: "8px" }}
          >
            <p className="errorMessageStyles py-1">{errorMessages}</p>
          </div>
        )}
      </div>
    );
  },
);

export default CustomInputs;
